import styled, { css } from 'styled-components/macro';
import Button from 'components/button';
import { IButtonProps } from 'components/button/Button';
import { screenMd } from 'constants/styles/breakpoints';
import Switch from 'components/switch';
import { compactSwitch } from 'components/switch/styles';
import { compactCheckbox } from 'components/checkbox/styles';
import { colors } from 'constants/styles/colors';
import { fontFamilyFallback } from 'constants/styles/fonts';

export const AdviseContainer = styled.div`
  background: linear-gradient(
    112.2deg,
    rgba(187, 188, 199, 0.4) -46.39%,
    rgba(187, 188, 199, 0) 112.38%
  );
  border-radius: 10px;
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-start;
  padding: 15px;
  margin: 10px;
  > svg {
    width: 20px !important;
    height: 20px !important;
  }

  ${screenMd} {
    margin: 0 0 20px 0;
    > .advice-inner-container {
      margin-left: 20px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex: 1;
    }
  }
`;

export const AdviseInfo = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 16px;
  line-height: 23px;
  margin: 0 10px 10px 0;

  ${screenMd} {
    margin-bottom: 0;
  }
`;

export const GetMoreLicensesButton = styled(Button)<IButtonProps>`
  &&& {
    background-color: ${({ theme }) => theme.default.buttons.primary.iddle.background};
    color: ${({ theme }) => theme.default.buttons.primary.iddle.color};
    &:active {
      background-color: ${({ theme }) => theme.default.buttons.primary.pressed.background};
      border-color: ${({ theme }) => theme.default.buttons.primary.pressed.background};
    }
    &:disabled {
      background-color: ${({ theme }) => theme.default.buttons.primary.disabled.background};
      color: ${({ theme }) => theme.default.buttons.primary.disabled.color};
    }
    width: 214px;
  }
`;

// The Skill Dive table's inset: 20px without teams, 10px with them.
const skillDiveInset = (teamGaps?: boolean) => (teamGaps ? 10 : 20);

export const Container = styled.div<{ $skillDiveLayout?: boolean; $teamGaps?: boolean }>`
  border-radius: 10px;
  padding: 10px;
  width: 100%;
  background: ${colors.dataVisualization.gray.light};
  overflow: auto;
  ${screenMd} {
    padding: 20px;
    max-height: 250px;
  }
  /* Takes whatever height the modal has left and scrolls the rest under the pinned labels. */
  ${({ $skillDiveLayout, $teamGaps }) =>
    $skillDiveLayout &&
    css`
      ${screenMd} {
        padding: ${skillDiveInset($teamGaps)}px;
        max-height: none;
        min-height: 0;
      }
      &::-webkit-scrollbar {
        width: 7px;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 10px;
        background: ${colors.dataVisualization.gray.dark};
      }
      &::-webkit-scrollbar-button {
        display: none;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
      }
      ${compactSwitch}
    `}
`;

export const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 0 10px;
  ${screenMd} {
    padding: 0;
  }
`;

export const BodyTitle = styled.div`
  color: ${colors.dataVisualization.black.dark};
  font-size: 20px;
  font-style: normal;
  font-weight: 900;
  line-height: 26px;
  letter-spacing: 0px;
  text-align: left;
  margin-bottom: 12px;
  ${screenMd} {
    margin-bottom: 20px;
  }
`;

// `$columns` is the Skill Dive step's shared template (see `skillDiveColumns`), so the header and
// the rows line up; without it the row keeps today's layout.
// With teams, the design leaves 20px before the Training and Skill Dive columns but none before
// the remove tile, so those two cells sit at the end of wider tracks.
const teamGapCells = css`
  > :nth-last-child(2),
  > :nth-last-child(3) {
    justify-self: end;
    width: 105px;
  }
`;

export const RowContainer = styled.div<{ $columns?: string; $teamGaps?: boolean }>`
  align-items: center;
  background: white;
  border-radius: 10px;
  border-width: 0px;
  border-style: solid;
  padding: 10px;
  display: grid;
  grid-template-columns: ${({ $columns }) => $columns || '60px 1fr 1fr 1fr'};
  ${({ $columns }) =>
    $columns &&
    css`
      column-gap: 0;
    `}
  ${({ $teamGaps }) => $teamGaps && teamGapCells}
  justify-content: start;

  > svg {
    margin-left: auto;
  }
  .actions-container {
    display: inline-flex;
    justify-content: flex-end;
    margin-left: auto;
    grid-gap: 30px;
  }
`;

export const HeaderRowContainer = styled.div<{ $columns?: string; $teamGaps?: boolean }>`
  display: grid;
  ${({ $teamGaps }) => $teamGaps && teamGapCells}
  grid-template-columns: ${({ $columns }) => $columns || '42% 20% 20% 18%'};
  margin-bottom: 10px;
  span {
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: ${colors.fonts.darkGray};
  }
  ${({ $columns, $teamGaps }) =>
    $columns &&
    css`
      /* The design's 28px label row, with the rows' 10px side padding. */
      align-items: center;
      column-gap: 0;
      min-height: 28px;
      margin: 0 0 10px;
      padding: 0 10px;
      span {
        font-weight: 400;
        font-size: 12px;
        line-height: 14px;
      }
      /* Pinned while the rows scroll. It takes over the box's top inset and the gap below as
         padding, so the rows pass under it rather than showing above or below it. */
      ${screenMd} {
        position: sticky;
        /* Sticky offsets count from inside the box's padding, so this pins it flush to the top. */
        top: -${skillDiveInset($teamGaps)}px;
        /* Above antd's z-index: 1 tick boxes in the rows passing underneath. */
        z-index: 2;
        margin: -${skillDiveInset($teamGaps)}px 0 0;
        padding: ${skillDiveInset($teamGaps)}px 10px 10px;
        min-height: ${28 + skillDiveInset($teamGaps) + 10}px;
        background: ${colors.dataVisualization.gray.light};
      }
    `}
`;

export const LabelEmail = styled.span`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0px;
  text-align: left;
  ${screenMd} {
    min-width: 250px;
  }
`;

export const SwitchContainer = styled.div`
  align-items: center;
  display: flex;
  margin-left: auto;
`;

export const SwitchManagerContainer = styled.div`
  align-items: center;
  display: flex;
  margin-left: auto;
`;

export const UserTeamContainer = styled.div<{ borderBottom?: boolean; width?: string }>`
  align-items: center;
  display: flex;
  justify-content: space-between;
  border-bottom: ${({ borderBottom }) => (borderBottom ? '1px solid #e9e9ed;' : 'none;')}
  padding-bottom: 2px;
  padding-top: 2px;
  ${({ width }) => (width ? `width: ${width}%;` : 'width: 100%;')};
`;

export const SwitchLabel = styled.label`
  color: #aeaeb2;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0px;
  margin-right: 15px;
  text-align: left;
`;

export const DeleteButtonContainer = styled.div``;

export const DeleteButton = styled.button`
  align-items: center;
  background: ${colors.iconButtons.gray.lighter};
  border-width: 0;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  height: 36px;
  width: 36px;
  margin-left: auto;
`;

export const EditContainer = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.default.colors.body.bg};
  display: flex;
  flex-direction: column;
  justify-content: start;
  flex-grow: 2;
  height: calc(100vh - 235px);
  > .invite-info {
    // letter and email
    display: flex;
    padding: 25px 10px;
    align-items: center;
    > span {
      font-weight: 500;
      font-size: 20px;
    }
  }
  > .invite-team {
    // custom team selector
    padding: 5px 10px;
    margin-bottom: 30px;
    max-width: 100%;
    svg {
      margin-left: auto !important;
    }
    > .ant-dropdown-trigger {
      background: #fff;
      box-shadow: 0px 5px 20px rgba(0, 0, 0, 0.2);
    }
  }
  > .invite-switchs {
    display: flex;
    width: 100%;
    padding: 10px;
    > div {
      margin-right: 50px;
      max-width: 120px;
      display: flex;
      flex-direction: column;
      justify-content: start;
      align-items: flex-start;
    }
  }
  > .invite-edit-footer {
    height: 83px;
    display: flex;
    justify-content: end;
    align-items: center;
    padding: 0 10px;
    position: fixed;
    bottom: 0;
    width: 100%;
    background: #fff;
    grid-gap: 10px;
  }
`;

export const EditFieldsLabel = styled.label`
  line-height: 14px;
  font-size: 16px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.default.colors.fonts.darkGray};
  margin-bottom: 10px;
`;

// Skill Dive invite UI. Kept after the existing components so their generated class names
// (numbered by position in this file) stay the same.
// Same wrapper as the playlist Skill Dive toggle, whose styles aren't exported.
export const SkillDiveToggleWrapper = styled.div<{ $nonClickable?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  ${({ $nonClickable }) =>
    $nonClickable &&
    css`
      cursor: not-allowed;
      * {
        cursor: not-allowed;
      }
    `}
`;

export const SkillDiveToggleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

export const SkillDiveSwitch = styled(Switch)<{ $nonClickable?: boolean }>`
  ${compactSwitch}
  ${({ $nonClickable }) =>
    $nonClickable &&
    css`
      .ant-switch {
        pointer-events: none;
      }
    `}
`;

export const GetSkillDiveLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.palette.Business_DataVizLilacDark};
  font-weight: 700;
  font-size: 12px;
  line-height: 14px;
`;

// A column label with its availability beneath, e.g. "Training" over "10 available". `$alert`
// turns a zero training count red.
export const HeaderAvailabilityCell = styled.div<{ $alert?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  > b {
    font-size: 12px;
    line-height: 14px;
    color: ${({ $alert }) => ($alert ? colors.performance.negativeDark : colors.fonts.dark)};
  }
`;

// Holds the Skill Dive column's width on rows that have no Skill Dive toggle.
export const SkillDivePlaceholder = styled.span`
  display: inline-block;
  width: 38px;
`;

// Avatar, email, [teams with their manager checkboxes,] training, Skill Dive, remove. Widths
// follow the design's 960px access step.
export const skillDiveColumns = (withTeams: boolean): string => {
  // 44px avatar plus the 15px the design leaves before the email.
  const toggles = withTeams ? ['165px', '125px', '125px'] : ['168.5px', '168.5px'];
  return ['59px', 'minmax(0, 1fr)', ...toggles, '36px'].join(' ');
};

// The invitee's teams, one line per team.
export const TeamsCell = styled.div``;

// The checkbox sits at the column's right edge, so the divider stops under it, as in the design.
export const TeamManagerRow = styled.div<{ $divider?: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 8px;
  align-items: center;
  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* The checkbox always renders a label span, which pads it away from the column's edge. */
  .ant-checkbox-wrapper > span:not(.ant-checkbox) {
    display: none;
  }
  ${compactCheckbox}
  padding: 2px 0;
  /* The list sits flush in the card, as drawn: no padding above the first team or below the last. */
  &:first-child {
    padding-top: 0;
  }
  &:last-child {
    padding-bottom: 0;
  }
  border-bottom: ${({ $divider }) =>
    $divider ? `1px solid ${colors.dataVisualization.gray.dusty}` : 'none'};
  font-size: 12px;
  line-height: 14px;
  color: ${colors.palette.Business_LightThemeGreyDarker};
`;

// The header's "Team" label sits 6px in from the team names below it, as drawn.
export const HeaderTeamManagerRow = styled(TeamManagerRow)`
  > span:first-child {
    padding-left: 6px;
  }
`;

export const CenteredCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const EndCell = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

// A long email is cut with an ellipsis instead of running into the team column.
export const SkillDiveEmail = styled(LabelEmail)`
  min-width: 0;
  font-family: 'Circular Std', ${fontFamilyFallback};
  font-weight: 700;
  /* Circular Std's tabular set, which antd turns on, spaces out the dots in an email. */
  font-feature-settings: normal;
  font-variant-numeric: normal;
  color: ${colors.fonts.dark};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${screenMd} {
    min-width: 0;
  }
`;

// The design's "no licenses available" banner: a red left edge, a pale red background and a dark
// call to action.
export const LicenseBannerContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding: 15px;
  /* 70% and 5% of the negative red, as drawn. */
  border-left: 6px solid ${colors.performance.negative}B3;
  border-radius: 6px;
  background: ${colors.performance.negative}0D;
  > svg {
    flex-shrink: 0;
  }
  > button {
    flex-shrink: 0;
    flex-basis: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }
  /* On a phone the button takes its own line under the text; wider screens keep one row, with
     the design's 50px before the button. */
  ${screenMd} {
    flex-wrap: nowrap;
    > button {
      flex-basis: auto;
      margin-left: auto;
    }
  }
`;

export const LicenseBannerText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  /* The design wraps the copy at 457px, leaving at least 50px before the button. */
  ${screenMd} {
    max-width: 457px;
    margin-right: 40px;
  }
  > b {
    color: ${colors.fonts.dark};
    font-size: 16px;
    font-weight: 900;
    line-height: 23px;
  }
  > p {
    margin: 0;
    color: ${colors.palette.Business_LightThemeGreyDarker};
    font-size: 14px;
    line-height: 20px;
  }
`;

// "Skill Dive" with its info icon beside it, for the not-on-plan header.
export const HeaderLabelWithInfo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  > span[role='img'] {
    display: inline-flex;
    cursor: help;
  }
`;

// Name over email, for existing users' rows.
export const NameEmailCell = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-size: 14px;
  line-height: 20px;
  > b,
  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  > b {
    color: ${colors.fonts.dark};
  }
  > span {
    color: ${colors.palette.Business_LightThemeGreyDarker};
  }
`;
